import { useState } from "react";
import { COLORS, BG_BASE } from "../../utils/colors";

const sampleData = {
  overallScore: 7.2,
  assessment: "Good",
  actionItems: [
    { id: 1, severity: "yellow", risk: "Cortez Negative DIR",      shortRisk: "Below-average delivery track record across 28 projects",    action: "Assign Korando as peer reviewer for Cortez deliverables during weeks 1-3. Establish weekly quality check-ins with engagement manager. Set explicit deliverable milestones with sign-off gates.", owner: "Engagement Manager + Korando", timing: "Pre-kickoff + weeks 1-3",     success: "Deliverables pass first review without rework in 2 of 3 milestones", dimension: "Experience" },
    { id: 2, severity: "yellow", risk: "PMP Certification Gap",    shortRisk: "SRE role requires PMP — no team member holds this cert",     action: "Confirm with client whether PMP is contractually required or preferred. Monske's CBAP and Lean Six Sigma Black Belt may satisfy the intent of the requirement.",                              owner: "Resource Manager",            timing: "Before staffing commitment", success: "Client confirms cert requirement is satisfied",                        dimension: "Technical"  },
    { id: 3, severity: "yellow", risk: "Below-Historical Pricing", shortRisk: "Korando and Monske priced 18-28% below their typical rates", action: "Validate with sales owner that role levels align with staffing seniority. Confirm this is a deliberate pricing strategy.",                                                                            owner: "Resource Manager + Sales",    timing: "Before proposal submission", success: "Written confirmation that pricing is intentional",                     dimension: "Financial"  },
    { id: 4, severity: "yellow", risk: "Staggered Start Gap",      shortRisk: "4-week gap between first and last team member starting",    action: "Use Korando's solo weeks for environment setup, client relationship building, and documentation prep. Create onboarding package for Cortez and Monske before they join.",              owner: "Korando + Engagement Manager", timing: "Week 1 planning",            success: "Onboarding doc ready before Cortez joins Oct 15",                     dimension: "Availability" },
  ],
  greenSignals: [
    { text: "100% collaboration pair coverage", dimension: "Chemistry"    },
    { text: "Korando prior client experience",  dimension: "Experience"   },
    { text: "Monske DIR +1.49 (exceptional)",   dimension: "Experience"   },
    { text: "2 of 3 team members 100% available", dimension: "Availability" },
  ],
  recommendation: "Staff this team with confidence. Watch Cortez's delivery quality closely and validate cost data before finalizing margins.",
};

function ActionItemCard({ item, isExpanded, onToggle }) {
  const borderColor  = item.severity === "red" ? COLORS.red : COLORS.amber;
  const severityLabel = item.severity === "red" ? "RED FLAG" : "ACTION ITEM";
  return (
    <div style={{ borderLeft: `3px solid ${borderColor}`, background: `${borderColor}06`, borderRadius: "0 6px 6px 0", marginBottom: 8, overflow: "hidden" }}>
      <div onClick={onToggle} style={{ padding: "10px 12px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
            <span style={{ fontSize: 8, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: borderColor }}>{severityLabel}</span>
            <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 6, background: `${COLORS.purple}20`, color: COLORS.purple }}>{item.dimension}</span>
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, lineHeight: 1.3 }}>{item.risk}</div>
          {!isExpanded && <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 3, lineHeight: 1.4 }}>{item.shortRisk}</div>}
        </div>
        <span style={{ color: COLORS.textMuted, fontSize: 10, flexShrink: 0, marginLeft: 8, marginTop: 2, transform: isExpanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>▼</span>
      </div>
      {isExpanded && (
        <div style={{ padding: "0 12px 12px" }}>
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 9, fontWeight: 600, color: COLORS.teal, textTransform: "uppercase", letterSpacing: 1, marginBottom: 3 }}>Mitigation</div>
            <div style={{ fontSize: 11, color: COLORS.text, lineHeight: 1.5 }}>{item.action}</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 10 }}>
            <div><span style={{ color: COLORS.textMuted }}>Owner: </span><span style={{ color: COLORS.text }}>{item.owner}</span></div>
            <div><span style={{ color: COLORS.textMuted }}>Timing: </span><span style={{ color: COLORS.text }}>{item.timing}</span></div>
          </div>
          <div style={{ marginTop: 6 }}>
            <span style={{ fontSize: 10, color: COLORS.textMuted }}>Success: </span>
            <span style={{ fontSize: 10, color: COLORS.green }}>{item.success}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ConciergeInsights({ data }) {
  const [expandedItem, setExpandedItem] = useState(0);
  const [showGreenSignals, setShowGreenSignals] = useState(true);
  const d = data;
  if (!d) return (
    <div style={{ padding: "32px 16px", textAlign: "center", color: "rgba(255,255,255,0.3)", fontSize: 12 }}>
      Run analysis to see Concierge insights
    </div>
  );

  const score = d.overallScore || 0;
  const assessment = d.assessment || (score >= 8 ? "Strong" : score >= 7 ? "Good" : score >= 5 ? "Adequate" : "Marginal");
  const scoreColor = score >= 7 ? COLORS.green : score >= 5 ? COLORS.amber : COLORS.red;

  const actionItems  = d.actionItems  || [];
  const greenSignals = d.greenSignals || [];

  return (
    <div style={{
      background: COLORS.cardBg, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, overflow: "hidden",
      height: "100%", display: "flex", flexDirection: "column",
    }}>
      {/* Header */}
      <div style={{ padding: "14px 16px 12px", borderBottom: `1px solid ${COLORS.cardBorder}`, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <span style={{ fontSize: 16 }}>💡</span>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, margin: 0 }}>Concierge Insights</h3>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: BG_BASE, borderRadius: 6 }}>
          <div>
            <div style={{ fontSize: 10, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1 }}>Team Score</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: scoreColor, lineHeight: 1.1 }}>
              {score.toFixed(1)}
            </div>
          </div>
          <div style={{ padding: "4px 10px", borderRadius: 12, background: `${scoreColor}20`, color: scoreColor, fontSize: 11, fontWeight: 600 }}>
            {assessment}
          </div>
        </div>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: "auto", padding: "12px" }}>
        {actionItems.length > 0 && (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.amber, textTransform: "uppercase", letterSpacing: 1 }}>
                🎯 {actionItems.length} Action Item{actionItems.length !== 1 ? "s" : ""}
              </div>
              <div style={{ fontSize: 10, color: COLORS.textMuted }}>
                {actionItems.filter(i => i.severity === "red").length > 0
                  ? `${actionItems.filter(i => i.severity === "red").length} red flag${actionItems.filter(i => i.severity === "red").length > 1 ? "s" : ""}`
                  : "No red flags"}
              </div>
            </div>
            {actionItems.map((item, i) => (
              <ActionItemCard key={item.id || i} item={item} isExpanded={expandedItem === i} onToggle={() => setExpandedItem(expandedItem === i ? null : i)} />
            ))}
          </>
        )}

        {greenSignals.length > 0 && (
          <>
            <div style={{ height: 1, background: COLORS.cardBorder, margin: "12px 0" }} />
            <div onClick={() => setShowGreenSignals(!showGreenSignals)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.green, textTransform: "uppercase", letterSpacing: 1 }}>
                ✓ {greenSignals.length} Green Signal{greenSignals.length !== 1 ? "s" : ""}
              </div>
              <span style={{ color: COLORS.textMuted, fontSize: 10, transform: showGreenSignals ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>▼</span>
            </div>
            {showGreenSignals && (
              <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 8 }}>
                {greenSignals.map((gs, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 8px", borderRadius: 4, background: `${COLORS.green}08` }}>
                    <span style={{ color: COLORS.green, fontSize: 10, flexShrink: 0 }}>✓</span>
                    <span style={{ fontSize: 10, color: COLORS.text, lineHeight: 1.4 }}>{gs.text}</span>
                    {gs.dimension && (
                      <span style={{ fontSize: 8, padding: "1px 4px", borderRadius: 4, flexShrink: 0, background: `${COLORS.purple}15`, color: COLORS.purple, marginLeft: "auto" }}>{gs.dimension}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {d.recommendation && (
          <>
            <div style={{ height: 1, background: COLORS.cardBorder, margin: "8px 0 12px" }} />
            <div style={{ padding: "10px 12px", borderRadius: 6, background: `${COLORS.purple}08`, borderLeft: `3px solid ${COLORS.purple}` }}>
              <div style={{ fontSize: 9, fontWeight: 600, color: COLORS.purple, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Recommendation</div>
              <div style={{ fontSize: 11, color: COLORS.text, lineHeight: 1.5 }}>{d.recommendation}</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
